import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import API from '../services/api';
import COMPANY_INFO from '../data/companyInfo';
import SignatureLines from './SignatureLines';
import ClientBlock from './ClientBlock';
import './InvoicePrintView.css';
import printWhenReady from '../utils/printWhenReady';
import amountInWords from '../utils/amountInWords';
import { formatDay } from '../utils/dates';
import Loading from './Loading';
import { useFileName, documentName, DOC_TYPES } from '../utils/fileName';

const money = (n) => new Intl.NumberFormat('es-DO', { style: 'currency', currency: 'DOP' }).format(n || 0);
const METHOD = { efectivo: 'Efectivo', transferencia: 'Transferencia', tarjeta: 'Tarjeta', cheque: 'Cheque', saldo: 'Saldo a favor' };
// A recibo that doesn't (or no longer does) stand for money received says so across the page.
const STAMP = { pending: 'POR VERIFICAR', rejected: 'DENEGADO', voided: 'ANULADO' };

const DISCLAIMER = 'Este recibo hace constar el pago recibido. No es una factura ni un comprobante fiscal. Los pagos con cheque quedan sujetos a que el banco los haga efectivos.';

// The printable recibo de ingreso for a client (Ingresos → Cobros registrados / Transportes → Imprimir). One ingreso can pay
// several facturas and leave or use saldo a favor; GET /payments/:id/receipt puts it together.
const IngresoPrintView = () => {
  const { id } = useParams();
  const [r, setR] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    API.get(`/payments/${id}/receipt`).then(res => setR(res.data)).catch(err => setError(err.response?.data?.message || 'No se pudo cargar el recibo.'));
  }, [id]);

  useEffect(() => {
    if (!r) return undefined;
    let cancelPrint = () => {};
    const t = setTimeout(() => { cancelPrint = printWhenReady(); }, 350);
    return () => { clearTimeout(t); cancelPrint(); };
  }, [r]);

  // Saved as PDF: <Cliente>ReciboDeIngreso / ReciboDeTransporte (utils/fileName.js).
  useFileName(r && documentName(r.client?.name, r.kind === 'transport' ? DOC_TYPES.reciboTransporte : DOC_TYPES.reciboIngreso));

  if (error) return <div className="p-4 text-danger">{error}</div>;
  if (!r) return <Loading page label="Cargando recibo..." />;

  const stamp = STAMP[r.status];
  const received = r.cash; // new money (payments, anticipo, transporte); saldo a favor used isn't money received
  const concept = r.kind === 'transport'
    ? (r.notes || 'Servicio de transporte (entrega / retiro)').replace(/\s*·\s*SGC2000.*$/, '')
    : r.invoices.length ? `Pago de ${r.invoices.length === 1 ? 'la factura' : 'las facturas'} ${r.invoices.map(i => i.number).join(', ')}`
      : 'Anticipo (saldo a favor)';

  return (
    <div className="invoice-page-wrapper">
      <div className="no-print text-center mb-3">
        <button className="btn btn-primary" onClick={() => window.print()}>
          <i className="bi bi-printer" /> Imprimir / Guardar como PDF
        </button>
      </div>

      <div className="invoice-paper" style={{ position: 'relative' }}>
        {stamp && (
          <div aria-hidden="true" style={{ position: 'absolute', top: '38%', left: 0, right: 0, textAlign: 'center', transform: 'rotate(-18deg)',
            fontSize: '4.5rem', fontWeight: 800, color: 'rgba(237,25,35,0.18)', letterSpacing: '0.2em', pointerEvents: 'none' }}>
            {stamp}
          </div>
        )}
        <div className="invoice-body">
          <div className="invoice-header-row">
            <div className="invoice-company-block">
              <img src="/logo-print.png" alt="Intmed" className="invoice-logo" />
              <div style={{ fontWeight: 700, color: '#0E1A24' }}>{COMPANY_INFO.name}</div>
              <div>RNC: {COMPANY_INFO.rnc}</div>
              <div>{COMPANY_INFO.address}</div>
              <div>{COMPANY_INFO.phone} · {COMPANY_INFO.email}</div>
              <div>{COMPANY_INFO.website}</div>
            </div>
            <div className="invoice-meta-block">
              <div className="invoice-title">{r.kind === 'transport' ? 'RECIBO DE TRANSPORTE' : 'RECIBO DE INGRESO'}</div>
              <div>No.: {r.number}</div>
              <div>Fecha: {formatDay(r.date)}</div>
              {stamp && <div style={{ color: '#B91C1C', fontWeight: 700 }}>{stamp}</div>}
            </div>
          </div>

          {r.client ? <ClientBlock label="Recibimos de" client={r.client} address={r.client.address} /> : (
            <div className="invoice-bill-to"><div className="label">Recibimos de</div><div>—</div></div>
          )}

          <table className="invoice-table">
            <tbody>
              <tr><td style={{ width: '28%', fontWeight: 600 }}>La suma de</td><td style={{ fontWeight: 600 }}>{amountInWords(received)}</td><td className="text-end" style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{money(received)}</td></tr>
              <tr><td style={{ fontWeight: 600 }}>Por concepto de</td><td colSpan={2}>{concept}</td></tr>
              <tr>
                <td style={{ fontWeight: 600 }}>Forma de pago</td>
                <td colSpan={2}>{METHOD[r.method] || r.method || '—'}{r.accountName ? ` — ${r.accountName}` : ''}</td>
              </tr>
            </tbody>
          </table>

          {r.invoices.length > 0 && (
            <table className="invoice-table">
              <thead>
                <tr>
                  <th>Factura</th><th>NCF</th><th>Fecha</th>
                  <th className="text-end">Total factura</th><th className="text-end">Pagado con este recibo</th><th className="text-end">Pendiente</th>
                </tr>
              </thead>
              <tbody>
                {r.invoices.map(i => (
                  <tr key={i.number}>
                    <td>{i.number}</td>
                    <td>{i.ncf || '—'}</td>
                    <td>{formatDay(i.date)}</td>
                    <td className="text-end">{money(i.total)}</td>
                    <td className="text-end">{money(i.applied + i.fromCredit)}{i.fromCredit > 0 && <div style={{ fontSize: '0.8em' }}>({money(i.fromCredit)} con saldo a favor)</div>}</td>
                    <td className="text-end">{i.balance === null ? '—' : money(i.balance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className="invoice-totals">
            <div className="invoice-totals-box">
              {r.invoices.length > 0 && <div className="invoice-totals-row"><span>Aplicado a facturas</span><span>{money(r.invoices.reduce((s, i) => s + i.applied + i.fromCredit, 0))}</span></div>}
              {r.creditUsed > 0 && <div className="invoice-totals-row"><span>Pagado con saldo a favor</span><span>−{money(r.creditUsed)}</span></div>}
              {r.advance > 0 && <div className="invoice-totals-row"><span>Queda como saldo a favor</span><span>{money(r.advance)}</span></div>}
              <div className="invoice-totals-row grand"><span>Total recibido</span><span>{money(received)}</span></div>
            </div>
          </div>

          {r.status === 'voided' && r.voidReason && (
            <div className="invoice-bill-to"><div className="label">Motivo de la anulación</div><div>{r.voidReason}</div></div>
          )}
          {r.status === 'pending' && (
            <div className="invoice-disclaimer">Este pago está pendiente de verificación por Intmed: el recibo es válido una vez verificado.</div>
          )}

          <div className="invoice-disclaimer">{DISCLAIMER}</div>
        </div>
        <div className="invoice-bottom">
          <SignatureLines showClient={false} />
          <div className="invoice-footer-note">Gracias por su pago — {COMPANY_INFO.name}{r.createdBy ? ` · Registrado por ${r.createdBy}` : ''}</div>
        </div>
      </div>
    </div>
  );
};

export default IngresoPrintView;
